import { Injector, Inject } from '@angular/core';
import { Router, ActivatedRoute } from '@angular/router';
import { HttpHeaders } from '@angular/common/http';
import { take } from 'rxjs/operators';
import { APIService } from 'app/services/api.service';
import { AppConfig } from "app/app.config";
import { AuthenticationService, authUserType } from 'app/services/auth.service';
import { LoaderService } from 'app/services/loader.service';
import {login} from "app/_helpers/login";

export type apiTokenProviderType = {
  Name: string;
  Description: string;
  IsActive: boolean;
  Email: null | string;
  PhoneNumber: string;
};

type apiMultipleChoiceQuestionType = {
  Id: number;
  Question: string;
  Answer1: string;
  Answer2: string;
  Answer3: string;
  Answer4: string;
  Answer: string;
};

// This type is not exhaustive, it contains what is set on the server, not what comes from the client and gets also returned
export type apiPasswordResetType = {
  Code: string;
  EmailType?: string;
  MultipleChoiceQuestions?: apiMultipleChoiceQuestionType[];
  DisplayName?: string;
  FirstName?: string;
  tokenProviders?: apiTokenProviderType[];
};

// This type is not exhaustive, it contains what is set on the server, not what comes from the client and gets also returned
export type apiSuccessfulPasswordResetType = {
  Code: string;
  EmailType: string;
  MultipleChoiceQuestions: apiMultipleChoiceQuestionType[];
  DisplayName: string;
  FirstName: string;
  tokenProviders: apiTokenProviderType[];
};

type apiLoginResponseType = string | authUserType | apiTokenProviderType[] | apiPasswordResetType;

type apiErrorType = {
  UserMessage: string;
  LockOutEndsMessage?: string;
  MaxAttempt?: number;
  CurrentAttempt?: number;
};

type apiErrorResponseType = {
  error: apiErrorType;
}

export class LoginCoreComponent {
  protected api: APIService;
  public config: AppConfig;
  protected router: Router;
  protected route: ActivatedRoute;
  protected auth: AuthenticationService;
  protected loaderService: LoaderService;

  public username = '';
  public email = '';
  public password = '';

  public returnUrl: string | null = null;
  public sessionTimedOut = false;
  public errorMsg = '';
  public sentcode=false;
  public TfaProviders: apiTokenProviderType[] = [];

  protected openContinueOnModal: ((url: string) => void) | null = null;

  constructor(@Inject(Injector) injector: Injector) {
    this.api = injector.get(APIService);
    this.config = injector.get(AppConfig);
    this.router = injector.get(Router);
    this.route = injector.get(ActivatedRoute);
    this.auth = injector.get(AuthenticationService);
    this.loaderService = injector.get(LoaderService);
  }

  post(api: string, params = {}, callback: null | ((data: apiLoginResponseType) => void) = null, error_callback: null | ((error: apiErrorResponseType) => void) = null) {
    this.loaderService.display(true,'login');
    this.api.post<apiLoginResponseType>(api, params, 
      { withCredentials: true, headers: new HttpHeaders({"content-type": "application/json"}) })
    .pipe(take(1)).subscribe(
      data => {
        let hideLoader = true;
        if (data === 'TemporaryDownTime') {
          this.router.navigate(['/error/client/7'], { skipLocationChange: true });
        }
        else if (data && Object.prototype.hasOwnProperty.call(data, 'token_type')) { // Logged in
          const authData = data as authUserType;
          setTimeout(()=>{this.authorize(authData)},0); // allow callback below to run first
          hideLoader = false;
        }
        else if(Array.isArray(data) && data.length > 0 && Object.prototype.hasOwnProperty.call(data[0],'Email') && this.sentcode==false) { // 2FA
          this.TfaProviders = data as apiTokenProviderType[];
          sessionStorage.setItem('2fa_username', this.username);
          this.sentcode = true;
        }
        if (typeof callback === 'function') {
          callback(data);
        }
        if (hideLoader) this.loaderService.display(false,'login');
      },
      (error: apiErrorResponseType) => {
        const pass = this.handleSubmitError(error.error);
        if (pass && (typeof error_callback === 'function')) {
          error_callback(error);
        }
        this.loaderService.display(false,'login');
      });
  }

  tfaTimeOut() {
    this.errorMsg = "Sorry, your session has expired. Please restart the login process";
    this.password = "";
    this.sentcode = false;
  }

  tfaPassed(user: authUserType) {
    this.sentcode=false;
    this.authorize(user);
  }

  authorize(user: authUserType) {
    if (user && user.token_type) {

      this.auth.logUserIn(user).subscribe(() => {
        login(this.auth, this.router, this.route, user,  this.returnUrl, this.openContinueOnModal);
        this.loaderService.display(false,'login');
      });
    } else {
      this.loaderService.display(false,'login');
    }
  }

  handleSubmitError(error: apiErrorType) {
    switch (error.UserMessage) {
      // cases 'Account lockedout.', 'Account lockedout with reset.' are taken care of in error.interceptor.ts
      case 'Invalid login attempt.':
        if (error.LockOutEndsMessage) {
          this.errorMsg = 'Exceeded attempt limit. Please try again after ' + error.LockOutEndsMessage + ' or go to forgot username or password.';
        } else {
          if (error.MaxAttempt && error.CurrentAttempt) {
            this.errorMsg = '\nInvalid username or password (' + (error.MaxAttempt - error.CurrentAttempt) + ' more attempt(s)).';
          } else {
            this.errorMsg = 'Invalid credentials, please try again.';
          }
        }
        return true;
        break;
      case 'Password already in use.':
        this.errorMsg = 'Sorry, this password has already been used.\nPlease choose a different password.';
        return true;
        break;
      case 'Invalid username and email combination.':
        this.errorMsg = 'Sorry, the username and email address you have provided do not match our records.';
        return true;
        break;

      case 'Invalid multiple choice answers.':
        this.router.navigate(['/error/client/1']);
        break;
      case 'Invalid or expired link.':
        this.router.navigate(['/error/client/2']);
        break;
      case 'ForgotUsername - Account setup incomplete.':
      case 'ForgotPassword - Account setup incomplete.':
        this.router.navigate(['/error/client/5']);
        break;
      case 'Duplicate emails found.':
        this.router.navigate(['/error/client/6']);
        break;
      case 'Internal server error.':
        this.router.navigate(['/error/client/9'], { skipLocationChange: true });
        break;
      case '':
        this.errorMsg = 'Something went wrong. Please try again.';
        break;
      default:
        this.errorMsg = error.UserMessage;
        return true;
    }
    return false;
  }

}
